import {Test, TestResult} from '../types/api';
import {BiomarkerStatus} from '../types/biomarker';
import {getCustomBiomarkerConfig} from './customBiomarkers';

export interface ProcessedBiomarker {
    testName: string;
    result: string;
    units: string;
    refRange: string;
    abnormal?: string;
    statusDate: string;
    status: BiomarkerStatus;
    progressBarPosition: number;
}

/**
 * Clean biomarker/test name by removing numerical prefixes from ELIS data
 * Examples:
 * - "01; Total IgE" -> "Total IgE"
 * - "02; WBC" -> "WBC"
 * - "Cholesterol" -> "Cholesterol" (unchanged)
 */
export const cleanBiomarkerName = (testName: string): string => {
    if (!testName) return testName;
    // Remove prefix pattern like "01; ", "02; ", etc.
    return testName.includes('; ') ? testName.split('; ')[1] : testName;
};

/**
 * Get the most recent biomarker from all test results
 * @param tests - Array of tests
 * @param gender - Optional gender for gender-specific configs
 */
export const getLatestBiomarker = (tests: Test[], gender?: 'male' | 'female'): ProcessedBiomarker | null => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    if (validResults.length === 0) return null;

    // Sort by date descending to get the most recent (use statusDate for biomarker timing, fallback to amendedDate, then createdDate)
    const sortedResults = validResults.sort((a, b) => {
        // Get the test data to access amendedDate and createdDate
        const aTest = tests.find((test) => test.results.includes(a));
        const bTest = tests.find((test) => test.results.includes(b));

        const aDate = new Date(a.statusDate || aTest?.amendedDate || aTest?.createdDate || '').getTime();
        const bDate = new Date(b.statusDate || bTest?.amendedDate || bTest?.createdDate || '').getTime();

        return bDate - aDate;
    });

    const latestResult = sortedResults[0];
    return processBiomarker(latestResult, gender);
};

/**
 * Get the top N most recent biomarkers for trends
 * @param tests - Array of tests
 * @param count - Number of biomarkers to return (default 4)
 * @param gender - Optional gender for gender-specific configs
 */
export const getRecentBiomarkers = (
    tests: Test[],
    count: number = 4,
    gender?: 'male' | 'female'
): ProcessedBiomarker[] => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    // Group by test name to get unique biomarkers
    const biomarkerMap = new Map<string, TestResult>();

    // Sort by date descending first
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    // Keep only the most recent result for each unique test
    sortedResults.forEach((result) => {
        if (!biomarkerMap.has(result.test_name)) {
            biomarkerMap.set(result.test_name, result);
        }
    });

    // Convert to processed biomarkers and take the requested count
    return Array.from(biomarkerMap.values())
        .slice(0, count)
        .map((result) => processBiomarker(result, gender));
};

/**
 * Format numeric result values by rounding to appropriate precision
 * Only shows decimal places if they are not zero
 */
export const formatResultValue = (value: string): string => {
    const numericValue = parseNumericValue(value);
    if (numericValue === null) return value; // Return original if not numeric

    // Round to 2 decimal places
    const rounded = Number(numericValue.toFixed(2));

    // If the number is a whole number, don't show decimal places
    if (rounded === Math.floor(rounded)) {
        return rounded.toString();
    }

    // Otherwise, show up to 2 decimal places (removing trailing zeros)
    return rounded.toString();
};

/**
 * Check if a test result is a binary test (Positive/Negative)
 * Binary tests have string results like "Positive", "Negative", "Detected", "Not Detected"
 * @param result - The test result string or TestResult object
 */
export const isBinaryTest = (result: string | TestResult): boolean => {
    const resultValue = typeof result === 'string' ? result : result?.result;
    if (!resultValue) return false;

    const lower = resultValue.toLowerCase().trim();
    return (
        lower === 'positive' ||
        lower === 'postive' || // Handle common typo
        lower === 'negative' ||
        lower === 'detected' ||
        lower === 'not detected'
    );
};

/**
 * Get the status for a binary test result
 * @param result - The test result string
 * @returns 'Positive' or 'Negative' status
 */
export const getBinaryTestStatus = (result: string): 'Positive' | 'Negative' => {
    const lower = result.toLowerCase().trim();
    if (lower === 'positive' || lower === 'postive' || lower === 'detected') {
        return 'Positive';
    }
    return 'Negative';
};

/**
 * Process a single test result into a standardized biomarker format
 * @param result - The test result to process
 * @param gender - Optional gender for gender-specific configs (e.g., HDL Direct)
 */
export const processBiomarker = (result: TestResult, gender?: 'male' | 'female'): ProcessedBiomarker => {
    // Always attempt status determination (uses 1-100 default range when missing)
    const status = determineStatus(result, gender);
    const progressBarPosition = calculateProgressBarPosition(result, gender);

    return {
        testName: result.test_name,
        result: formatResultValue(result.result),
        units: result.units || '',
        refRange: result.refRange || '',
        abnormal: result.abnormal,
        statusDate: result.statusDate || '',
        status,
        progressBarPosition,
    };
};

/**
 * Determine the status (Normal, High, Low, Optimal, Borderline, Elevated) of a biomarker result
 * Priority order:
 * 0. Binary tests (Positive/Negative) - return immediately for these
 * 1. Custom biomarker configurations with forceCustomRange=true (special display needs)
 * 2. API-provided reference range (source of truth when available)
 * 3. Custom biomarker configurations without forceCustomRange (fallback for missing API ranges)
 * 4. Default 1-100 range
 *
 * Note: We verify the 'abnormal' field from the backend against the actual reference range
 * to ensure accuracy, as backend data can sometimes be incorrect.
 */
export const determineStatus = (result: TestResult, gender?: 'male' | 'female'): BiomarkerStatus => {
    // Priority 0: Binary tests (Positive/Negative/Detected/Not Detected)
    if (isBinaryTest(result)) {
        return getBinaryTestStatus(result.result);
    }

    const customConfig = getCustomBiomarkerConfig(result.test_name, gender);
    const numericResult = parseNumericValue(result.result);

    // Priority 1: Custom config with forceCustomRange=true (special cases only)
    if (customConfig?.forceCustomRange && customConfig.zones.length > 0 && numericResult !== null) {
        // Find which zone the value falls into
        for (let i = 0; i < customConfig.zones.length; i++) {
            const zone = customConfig.zones[i];

            // Use >= for min and < for max, except for the last zone which uses <=
            const inZone =
                i === customConfig.zones.length - 1
                    ? numericResult >= zone.min && numericResult <= zone.max
                    : numericResult >= zone.min && numericResult < zone.max;

            if (inZone) {
                return zone.status;
            }
        }

        // If value is outside all zones, determine based on extremes
        const firstZone = customConfig.zones[0];
        const lastZone = customConfig.zones[customConfig.zones.length - 1];

        if (numericResult < firstZone.min) {
            return firstZone.status === 'Low' ? 'Low' : 'Low';
        }
        if (numericResult > lastZone.max) {
            return lastZone.status === 'High' ? 'High' : 'High';
        }
    }

    // Priority 2: API-provided reference range (source of truth)
    if (numericResult !== null) {
        const range = parseReferenceRange(result.refRange);

        if (range) {
            // Calculate status based on reference range (this is our source of truth)
            let calculatedStatus: 'Normal' | 'High' | 'Low';
            if (numericResult < range.min) {
                calculatedStatus = 'Low';
            } else if (numericResult > range.max) {
                calculatedStatus = 'High';
            } else {
                // Within reference range = Normal
                calculatedStatus = 'Normal';
            }

            // If abnormal field is provided, verify it matches our calculation
            // If it doesn't match, trust our calculation over the backend value
            if (result.abnormal) {
                const abnormalLower = result.abnormal.toLowerCase();
                let backendStatus: 'Normal' | 'High' | 'Low' | 'Unknown' = 'Unknown';

                if (abnormalLower.includes('high') || abnormalLower === 'h') {
                    backendStatus = 'High';
                } else if (abnormalLower.includes('low') || abnormalLower === 'l') {
                    backendStatus = 'Low';
                } else if (abnormalLower === 'normal' || abnormalLower === '') {
                    backendStatus = 'Normal';
                }

                // Only use backend status if it matches our calculation
                // This protects against incorrect backend data
                if (backendStatus === calculatedStatus) {
                    return backendStatus;
                }
                // Backend status doesn't match - use our calculated status
            }

            return calculatedStatus;
        } else {
            // Priority 3: Custom config as fallback when no API range available
            if (customConfig && customConfig.zones.length > 0) {
                // Find which zone the value falls into
                for (let i = 0; i < customConfig.zones.length; i++) {
                    const zone = customConfig.zones[i];

                    // Use >= for min and < for max, except for the last zone which uses <=
                    const inZone =
                        i === customConfig.zones.length - 1
                            ? numericResult >= zone.min && numericResult <= zone.max
                            : numericResult >= zone.min && numericResult < zone.max;

                    if (inZone) {
                        return zone.status;
                    }
                }

                // If value is outside all zones, determine based on extremes
                const firstZone = customConfig.zones[0];
                const lastZone = customConfig.zones[customConfig.zones.length - 1];

                if (numericResult < firstZone.min) {
                    return firstZone.status === 'Low' ? 'Low' : 'Low';
                }
                if (numericResult > lastZone.max) {
                    return lastZone.status === 'High' ? 'High' : 'High';
                }
            }

            // No reference range or custom config - fall back to backend abnormal field if present
            if (result.abnormal) {
                const abnormalLower = result.abnormal.toLowerCase();
                if (abnormalLower.includes('high') || abnormalLower === 'h') return 'High';
                if (abnormalLower.includes('low') || abnormalLower === 'l') return 'Low';
                if (abnormalLower === 'normal') return 'Normal';
            }

            // Use 1-100 default range when reference range is missing and no abnormal field
            const defaultRange = {min: 1, max: 100};
            if (numericResult < defaultRange.min) return 'Low';
            if (numericResult > defaultRange.max) return 'High';
            return 'Normal';
        }
    }

    // Non-numeric result - use abnormal field if present
    if (result.abnormal) {
        const abnormalLower = result.abnormal.toLowerCase();
        if (abnormalLower.includes('high') || abnormalLower === 'h') return 'High';
        if (abnormalLower.includes('low') || abnormalLower === 'l') return 'Low';
        if (abnormalLower === 'normal') return 'Normal';
    }

    return 'Unknown';
};

/**
 * Calculate progress bar position (0-100) based on result and reference range
 * Priority order:
 * 1. Custom biomarker configurations with forceCustomRange=true (special display needs)
 * 2. API-provided reference range (source of truth when available)
 * 3. Custom biomarker configurations without forceCustomRange (fallback for missing API ranges)
 * 4. Default 1-100 range
 *
 * Standard 3-zone:
 * - 0-33.33%: Low zone (orange)
 * - 33.33-66.67%: Normal/Optimal zone (green) - this is where the reference range maps to
 * - 66.67-100%: High zone (pink)
 *
 * Custom zones (e.g., Cortisol with 10%-80%-10%):
 * - 0-10%: Below Range
 * - 10-90%: In Range
 * - 90-100%: Above Range
 *
 * 4-zone layout (e.g., Total Cholesterol):
 * - 0-25%: Low
 * - 25-50%: Optimal
 * - 50-75%: Borderline
 * - 75-100%: High
 */
export const calculateProgressBarPosition = (result: TestResult, gender?: 'male' | 'female'): number => {
    const numericResult = parseNumericValue(result.result);

    if (numericResult === null) return 50; // Default to middle if result is not numeric

    const customConfig = getCustomBiomarkerConfig(result.test_name, gender);

    // Priority 1: Custom config with forceCustomRange=true (special cases only)
    if (customConfig?.forceCustomRange && customConfig.zones.length > 0) {
        let cumulativePercentage = 0;

        // Find which zone the value falls into and calculate position within that zone
        for (let i = 0; i < customConfig.zones.length; i++) {
            const zone = customConfig.zones[i];
            const zoneStart = cumulativePercentage;

            // Use >= for min and < for max, except for the last zone which uses <=
            const inZone =
                i === customConfig.zones.length - 1
                    ? numericResult >= zone.min && numericResult <= zone.max
                    : numericResult >= zone.min && numericResult < zone.max;

            if (inZone) {
                // Value is within this zone
                const positionInZone = (numericResult - zone.min) / (zone.max - zone.min);
                const finalPosition = zoneStart + positionInZone * zone.percentage;
                return finalPosition;
            }

            cumulativePercentage += zone.percentage;
        }

        // If value is outside all zones, clamp to edges
        const firstZone = customConfig.zones[0];
        const lastZone = customConfig.zones[customConfig.zones.length - 1];

        if (numericResult < firstZone.min) {
            // Map to first portion of first zone (proportionally)
            const range = firstZone.max - firstZone.min;
            const distance = firstZone.min - numericResult;
            const proportionalPosition = Math.max(0, (range - distance) / range);
            return proportionalPosition * firstZone.percentage;
        }

        if (numericResult > lastZone.max) {
            // Map to last portion of last zone (proportionally)
            const totalBeforeLast = customConfig.zones.slice(0, -1).reduce((sum, z) => sum + z.percentage, 0);
            const range = lastZone.max - lastZone.min;
            const distance = numericResult - lastZone.max;
            const proportionalPosition = Math.min(1, (range + distance) / (range * 2));
            return totalBeforeLast + proportionalPosition * lastZone.percentage;
        }

        // Fallback to middle
        return 50;
    }

    // Priority 2: API-provided reference range (standard 3-zone logic)
    const range = parseReferenceRange(result.refRange);

    if (range) {
        const rangeWidth = range.max - range.min;

        // Define the three zones: Low (0-33.33%), Normal (33.33-66.67%), High (66.67-100%)
        const LOW_ZONE_END = 33.33;
        const NORMAL_ZONE_START = 33.33;
        const NORMAL_ZONE_END = 66.67;
        const HIGH_ZONE_START = 66.67;

        // If the value is within the reference range, map it to the normal zone (33.33-66.67%)
        if (numericResult >= range.min && numericResult <= range.max) {
            const positionInRange = (numericResult - range.min) / rangeWidth;
            const normalZoneWidth = NORMAL_ZONE_END - NORMAL_ZONE_START;
            return NORMAL_ZONE_START + positionInRange * normalZoneWidth;
        }

        // If the value is below the reference range, map it to the low zone (0-33.33%)
        if (numericResult < range.min) {
            // Extend the low zone to cover values down to (min - rangeWidth)
            const lowZoneMin = range.min - rangeWidth;
            const lowZoneMax = range.min;

            // Clamp to the low zone minimum
            const clampedValue = Math.max(numericResult, lowZoneMin);
            const positionInLowZone = (clampedValue - lowZoneMin) / (lowZoneMax - lowZoneMin);
            return positionInLowZone * LOW_ZONE_END;
        }

        // If the value is above the reference range, map it to the high zone (66.67-100%)
        if (numericResult > range.max) {
            // Extend the high zone to cover values up to (max + rangeWidth)
            const highZoneMin = range.max;
            const highZoneMax = range.max + rangeWidth;

            // Clamp to the high zone maximum
            const clampedValue = Math.min(numericResult, highZoneMax);
            const positionInHighZone = (clampedValue - highZoneMin) / (highZoneMax - highZoneMin);
            const highZoneWidth = 100 - HIGH_ZONE_START;
            return HIGH_ZONE_START + positionInHighZone * highZoneWidth;
        }

        // Fallback (should not reach here)
        return 50;
    }

    // Priority 3: Custom config as fallback when no API range available
    if (customConfig && customConfig.zones.length > 0) {
        let cumulativePercentage = 0;

        // Find which zone the value falls into and calculate position within that zone
        for (let i = 0; i < customConfig.zones.length; i++) {
            const zone = customConfig.zones[i];
            const zoneStart = cumulativePercentage;

            // Use >= for min and < for max, except for the last zone which uses <=
            const inZone =
                i === customConfig.zones.length - 1
                    ? numericResult >= zone.min && numericResult <= zone.max
                    : numericResult >= zone.min && numericResult < zone.max;

            if (inZone) {
                // Value is within this zone
                const positionInZone = (numericResult - zone.min) / (zone.max - zone.min);
                const finalPosition = zoneStart + positionInZone * zone.percentage;
                return finalPosition;
            }

            cumulativePercentage += zone.percentage;
        }

        // If value is outside all zones, clamp to edges
        const firstZone = customConfig.zones[0];
        const lastZone = customConfig.zones[customConfig.zones.length - 1];

        if (numericResult < firstZone.min) {
            // Map to first portion of first zone (proportionally)
            const range = firstZone.max - firstZone.min;
            const distance = firstZone.min - numericResult;
            const proportionalPosition = Math.max(0, (range - distance) / range);
            return proportionalPosition * firstZone.percentage;
        }

        if (numericResult > lastZone.max) {
            // Map to last portion of last zone (proportionally)
            const totalBeforeLast = customConfig.zones.slice(0, -1).reduce((sum, z) => sum + z.percentage, 0);
            const range = lastZone.max - lastZone.min;
            const distance = numericResult - lastZone.max;
            const proportionalPosition = Math.min(1, (range + distance) / (range * 2));
            return totalBeforeLast + proportionalPosition * lastZone.percentage;
        }
    }

    // Priority 4: Default 1-100 range when nothing else is available
    const defaultRange = {min: 1, max: 100};
    const position = ((numericResult - defaultRange.min) / (defaultRange.max - defaultRange.min)) * 100;
    return Math.max(0, Math.min(100, position));
};

/**
 * Parse a numeric value from a result string
 */
export const parseNumericValue = (value: string): number | null => {
    if (!value) return null;

    // Remove common non-numeric characters and extract number
    const cleaned = value.replace(/[<>≤≥]/g, '').trim();
    const match = cleaned.match(/[\d.]+/);

    if (match) {
        const parsed = parseFloat(match[0]);
        return isNaN(parsed) ? null : parsed;
    }

    return null;
};

/**
 * Parse reference range string (e.g., "30-100", "< 5.7", "2.5 - 4.5", "<10-75")
 */
export const parseReferenceRange = (refRange: string): {min: number; max: number} | null => {
    if (!refRange) return null;

    // Handle different reference range formats
    const cleaned = refRange.replace(/[≤≥]/g, '').trim();

    // Format: "min - max", "min-max", or "<min-max"
    const rangeMatch = cleaned.match(/<?\s*(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
    if (rangeMatch) {
        const min = parseFloat(rangeMatch[1]);
        const max = parseFloat(rangeMatch[2]);
        if (!isNaN(min) && !isNaN(max)) {
            return {min, max};
        }
    }

    // Format: "< value" (upper limit only)
    const upperMatch = cleaned.match(/[<]\s*(\d+\.?\d*)/);
    if (upperMatch) {
        const max = parseFloat(upperMatch[1]);
        if (!isNaN(max)) {
            return {min: 0, max};
        }
    }

    // Format: "> value" (lower limit only)
    const lowerMatch = cleaned.match(/[>]\s*(\d+\.?\d*)/);
    if (lowerMatch) {
        const min = parseFloat(lowerMatch[1]);
        if (!isNaN(min)) {
            return {min, max: min * 2}; // Approximate upper bound
        }
    }

    return null;
};

/**
 * Parse reference range with default fallback
 * Returns default range {min: 0, max: 100} if range cannot be parsed
 */
export const parseRefRangeWithDefault = (
    refRange: string,
    defaultRange: {min: number; max: number} = {min: 0, max: 100}
): {min: number; max: number} => {
    return parseReferenceRange(refRange) || defaultRange;
};

/**
 * Format test name by extracting the display name from compound names
 * e.g., "12345; Glucose" -> "Glucose"
 */
export const formatTestName = (testName: string): string => {
    if (testName && testName.includes('; ')) {
        return testName.split('; ')[1];
    }
    return testName;
};

/**
 * Format date for display
 */
export const formatTestDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
        });
    } catch {
        return dateString;
    }
};

/**
 * Calculate trend data for historical results of the same biomarker
 */
export const calculateBiomarkerTrend = (
    tests: Test[],
    testName: string
): Array<{name: string; value: number | null; dummy?: boolean}> => {
    const allResults = tests.flatMap((test) => test.results);
    const testResults = allResults
        .filter(
            (result) =>
                result.test_name === testName &&
                result.statusDate &&
                result.result &&
                result.result !== 'Pending' &&
                result.result !== 'N/A'
        )
        .sort((a, b) => new Date(a.statusDate!).getTime() - new Date(b.statusDate!).getTime());

    if (testResults.length === 0) return [];

    // Create chart data without padding
    const chartData: Array<{name: string; value: number | null; dummy?: boolean}> = [];

    // Take last 4 results for the trend
    const recentResults = testResults.slice(-4);

    recentResults.forEach((result) => {
        const numericValue = parseNumericValue(result.result);
        const dateLabel = formatTestDate(result.statusDate!);

        chartData.push({
            name: dateLabel,
            value: numericValue,
        });
    });

    return chartData;
};

/**
 * Calculate trend change for a biomarker (comparing latest vs previous result)
 */
export const calculateTrendChange = (
    tests: Test[],
    testName: string
): {changeText: string; changeType: 'up' | 'down'} => {
    const allResults = tests.flatMap((test) => test.results);
    const testResults = allResults
        .filter(
            (result) =>
                result.test_name === testName &&
                result.statusDate &&
                result.result &&
                result.result !== 'Pending' &&
                result.result !== 'N/A'
        )
        .sort((a, b) => new Date(a.statusDate!).getTime() - new Date(b.statusDate!).getTime());

    if (testResults.length < 2) {
        // If we only have one result or none, show the current value as neutral
        const currentValue = testResults.length > 0 ? testResults[testResults.length - 1].result : '0';
        return {
            changeText: currentValue,
            changeType: 'up', // Default to up for single values
        };
    }

    const latest = testResults[testResults.length - 1];
    const previous = testResults[testResults.length - 2];

    const latestValue = parseNumericValue(latest.result);
    const previousValue = parseNumericValue(previous.result);

    if (latestValue === null || previousValue === null) {
        return {
            changeText: latest.result,
            changeType: 'up',
        };
    }

    const change = latestValue - previousValue;
    const percentChange = previousValue !== 0 ? Math.abs((change / previousValue) * 100) : 0;

    return {
        changeText: `${percentChange.toFixed(1)}%`,
        changeType: change >= 0 ? 'up' : 'down',
    };
};

export interface MarkerStatusCounts {
    normal: number;
    borderline: number;
    critical: number;
}

/**
 * Categorize all biomarkers by their status for the markers overview chart
 * @param tests - Array of tests
 * @param gender - Optional gender for gender-specific configs
 */
export const categorizeBiomarkerStatuses = (tests: Test[], gender?: 'male' | 'female'): MarkerStatusCounts => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    // Get unique biomarkers (latest result for each test type)
    const biomarkerMap = new Map<string, TestResult>();
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    sortedResults.forEach((result) => {
        if (!biomarkerMap.has(result.test_name)) {
            biomarkerMap.set(result.test_name, result);
        }
    });

    // Categorize each unique biomarker
    let normal = 0;
    let borderline = 0;
    let critical = 0;

    biomarkerMap.forEach((result) => {
        const status = determineStatus(result, gender);

        switch (status) {
            case 'Normal':
            case 'Optimal':
                normal++;
                break;
            case 'Borderline':
            case 'Elevated':
                borderline++;
                break;
            case 'High':
            case 'Low':
                // Consider High/Low as borderline unless they're significantly out of range
                if (isSignificantlyOutOfRange(result)) {
                    critical++;
                } else {
                    borderline++;
                }
                break;
            default:
                // Unknown status - could be considered borderline
                borderline++;
                break;
        }
    });

    return {normal, borderline, critical};
};

/**
 * Determine if a result is significantly out of the normal range (critical vs borderline)
 */
export const isSignificantlyOutOfRange = (result: TestResult): boolean => {
    const numericResult = parseNumericValue(result.result);
    if (numericResult === null || !result.refRange) return false;

    const range = parseReferenceRange(result.refRange);
    if (!range) return false;

    const rangeWidth = range.max - range.min;
    const tolerance = rangeWidth * 0.25; // 25% outside range is considered critical

    // Check if result is significantly outside the range
    if (numericResult < range.min) {
        return range.min - numericResult > tolerance;
    }

    if (numericResult > range.max) {
        return numericResult - range.max > tolerance;
    }

    return false;
};

/**
 * Get all unique biomarkers from test results (one per test name, most recent)
 * Returns biomarkers sorted by date (most recent first)
 * @param tests - Array of tests
 * @param gender - Optional gender for gender-specific configs
 */
export const getAllUniqueBiomarkers = (tests: Test[], gender?: 'male' | 'female'): ProcessedBiomarker[] => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    // Group by test name to get unique biomarkers
    const biomarkerMap = new Map<string, TestResult>();

    // Sort by date descending first
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    // Keep only the most recent result for each unique test
    sortedResults.forEach((result) => {
        if (!biomarkerMap.has(result.test_name)) {
            biomarkerMap.set(result.test_name, result);
        }
    });

    // Convert to processed biomarkers
    return Array.from(biomarkerMap.values()).map((result) => processBiomarker(result, gender));
};

/**
 * Get a specific biomarker by test name (most recent result)
 * Returns the most recent result for the specified test name
 * @param tests - Array of tests
 * @param testName - The name of the test to find
 * @param gender - Optional gender for gender-specific configs
 */
export const getBiomarkerByName = (
    tests: Test[],
    testName: string,
    gender?: 'male' | 'female'
): ProcessedBiomarker | null => {
    const allResults = tests.flatMap((test) => test.results);
    const cleanedSearchName = cleanBiomarkerName(testName);

    // Try exact match first, then try matching cleaned names
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            (result.test_name === testName || cleanBiomarkerName(result.test_name) === cleanedSearchName) &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    if (validResults.length === 0) return null;

    // Sort by date descending to get the most recent
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    return processBiomarker(sortedResults[0], gender);
};
